import React, { useState } from 'react';
import { createUserWithEmailAndPassword } from "firebase/auth";
import { FaEye, FaEyeSlash, FaCheckCircle } from "react-icons/fa";
import { auth } from "../firebase";
import { Link, useNavigate } from 'react-router-dom';
import './Auth.css';
import GuideWell_Blue from '../Assets/RSIDE.png';
import buildingImg from '../Assets/building_manager.jpg';
import { showToast } from '../components/toast';
import { formatPhone } from '../components/formatPhone';

function SignUp() {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showRules, setShowRules] = useState(false);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    company: '',
    password: '',
    retypePassword: '',
    agreeTerms: false
  });

  /* ================= PASSWORD RULES ================= */
  const passwordRules = {
    length: formData.password.length >= 8,
    uppercase: /[A-Z]/.test(formData.password),
    lowercase: /[a-z]/.test(formData.password),
    number: /[0-9]/.test(formData.password),
    special: /[^A-Za-z0-9]/.test(formData.password),
  };

  const isPasswordValid = Object.values(passwordRules).every(Boolean);

  /* ================= HANDLERS ================= */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : name === 'phoneNumber' ? formatPhone(value) : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    /* -------- Frontend Validation -------- */
    if (!isPasswordValid) {
      showToast("Password does not meet all requirements", 'warning');
      return;
    }

    if (formData.password !== formData.retypePassword) {
      showToast("Passwords do not match", 'warning');
      return;
    }

    if (!formData.agreeTerms) {
      showToast("Please agree to the Terms of Service and Privacy Policy", 'warning');
      return;
    }

    try {
      /* -------- 1️⃣ Firebase Auth -------- */
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      console.log("Firebase user created:", userCredential.user.uid);

      /* -------- 2️⃣ Save to YOUR database -------- */
      const signupParams = new URLSearchParams({
        full_name: formData.fullName,
        email: formData.email,
        phone_number: formData.phoneNumber,
        company: formData.company,
        SignedUpFrom: 'website',
      });
      const response = await fetch(`https://api.rside.app/SignUp?${signupParams}`, {
        method: "POST",
        headers: { accept: 'application/json' },
      });

      const responseText = await response.text();

      console.log("API STATUS:", response.status);
      console.log("API RESPONSE:", responseText);

      if (!response.ok) {
        throw new Error("User NOT saved in database");
      }

      /* -------- 3️⃣ Success -------- */
      localStorage.setItem('isNewManager', 'true');
      localStorage.setItem(`isDemoMode_${formData.email}`, 'true');
      localStorage.setItem(`isDemoProps_${formData.email}`, 'true');
      localStorage.setItem(`isDemoActivity_${formData.email}`, 'true');
      localStorage.setItem(`isDemoWorkOrders_${formData.email}`, 'true');
      navigate("/home");

    } catch (error) {
      console.error("SIGNUP ERROR:", error);
      const friendlyErrors = {
        'auth/email-already-in-use': 'An account with this email already exists. Try logging in instead.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/weak-password': 'Password must be at least 6 characters.',
        'auth/network-request-failed': 'Network error. Please check your connection and try again.',
        'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
      };
      const msg = friendlyErrors[error.code] || (error.message === 'User NOT saved in database' ? 'Account created but profile setup failed. Please contact support.' : 'Something went wrong. Please try again.');
      showToast(msg, 'error');
    }
  };

  /* ================= UI ================= */
  return (
    <div className="auth-container auth-bg-full">
      <img src={buildingImg} alt="" className="auth-full-bg-img" />
      <div className="auth-full-overlay" />

      <div className="auth-inner">
      {/* Left branding */}
      <div className="auth-brand-side">
        <h1 className="auth-brand-tagline">Manage your<br />properties smarter.</h1>
        <p className="auth-brand-sub">Everything you need to run your properties — all in one place.</p>
        <ul className="auth-brand-features">
          <li><FaCheckCircle className="auth-feature-icon" /> Track rent &amp; payments</li>
          <li><FaCheckCircle className="auth-feature-icon" /> Manage work orders</li>
          <li><FaCheckCircle className="auth-feature-icon" /> Monitor vacancies</li>
          <li><FaCheckCircle className="auth-feature-icon" /> Communicate with tenants</li>
        </ul>
      </div>

      {/* Right form card — wider for 2-col fields */}
      <div className="auth-full-card-wrap auth-signup-wrap">
        <div className="auth-card auth-dark-card signup-card">
          <img src={GuideWell_Blue} alt="RSIDE" className="auth-logo" />
          <h2 className="auth-title">Create Account</h2>

          <form onSubmit={handleSubmit} className="auth-form">

            {/* Row 1 */}
            <div className="signup-row">
              <div className="form-group">
                <input type="text" name="fullName" placeholder="Full Name"
                  value={formData.fullName} onChange={handleChange} required className="form-input" />
              </div>
              <div className="form-group">
                <input type="email" name="email" placeholder="Email"
                  value={formData.email} onChange={handleChange} required className="form-input" />
              </div>
            </div>

            {/* Row 2 */}
            <div className="signup-row">
              <div className="form-group">
                <input type="tel" name="phoneNumber" placeholder="Phone Number"
                  value={formData.phoneNumber} onChange={handleChange} required className="form-input" />
              </div>
              <div className="form-group">
                <input type="text" name="company" placeholder="Company"
                  value={formData.company} onChange={handleChange} required className="form-input" />
              </div>
            </div>

            {/* Row 3 — password */}
            <div className="signup-row">
              <div className="form-group password-group"
                onFocus={() => setShowRules(true)} onBlur={() => setShowRules(false)}>
                <input type={showPassword ? "text" : "password"} name="password" placeholder="Password"
                  value={formData.password} onChange={handleChange} required className="form-input" />
                <span className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
                {showRules && (
                  <div className="password-rules">
                    <p className={passwordRules.length    ? "valid" : "invalid"}>• Minimum 8 characters</p>
                    <p className={passwordRules.uppercase ? "valid" : "invalid"}>• Uppercase letter</p>
                    <p className={passwordRules.lowercase ? "valid" : "invalid"}>• Lowercase letter</p>
                    <p className={passwordRules.number    ? "valid" : "invalid"}>• Number</p>
                    <p className={passwordRules.special   ? "valid" : "invalid"}>• Special character</p>
                  </div>
                )}
              </div>
              <div className="form-group">
                <input type="password" name="retypePassword" placeholder="Retype Password"
                  value={formData.retypePassword} onChange={handleChange} required className="form-input" />
              </div>
            </div>

            {/* Checkboxes */}
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input type="checkbox" name="agreeTerms" checked={formData.agreeTerms} onChange={handleChange} required />
                <span>I agree to the{' '}<a href="/terms" target="_blank" rel="noreferrer" className="auth-inline-link">Terms of Service</a>{' '}and{' '}<a href="/privacy" target="_blank" rel="noreferrer" className="auth-inline-link">Privacy Policy</a></span>
              </label>
            </div>

            <button type="submit" className="auth-button">Sign Up</button>
          </form>

          <div className="auth-links">
            <p>Already have an account? <Link to="/login">Log In</Link></p>
            <p><Link to="/select-role?mode=signup" className="forgot-password">← Back</Link></p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default SignUp;
